import * as THREE from 'three';

// Procedural facade textures — canvas-generated at runtime so the town gets
// material detail with zero asset files (POC rule). All textures are built
// once per variant and cached; per-building variation comes from `seed` and
// from texture.repeat (clones share the underlying image — cheap).

const cache = new Map();

// Deterministic PRNG so facades don't reshuffle on hot-reload.
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeCanvas(size) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  return c;
}

function toTexture(canvas) {
  const t = new THREE.CanvasTexture(canvas);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

// Running-bond brick, drawn in near-grayscale so the material's `color` prop
// tints it — one texture serves every building color.
export function brickTexture(seed = 1) {
  const key = `brick:${seed}`;
  if (cache.has(key)) return cache.get(key);

  const rnd = mulberry32(seed * 7919);
  const S = 256;
  const canvas = makeCanvas(S);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#8a8a8a'; // mortar
  ctx.fillRect(0, 0, S, S);

  const rows = 16;
  const cols = 8;
  const bh = S / rows;
  const bw = S / cols;
  for (let r = 0; r < rows; r++) {
    const offset = (r % 2) * (bw / 2);
    for (let c = -1; c < cols; c++) {
      const v = 165 + Math.floor(rnd() * 50); // per-brick value jitter
      ctx.fillStyle = `rgb(${v},${v - 6 + Math.floor(rnd() * 10)},${v - 12})`;
      ctx.fillRect(c * bw + offset + 1, r * bh + 1, bw - 2, bh - 2);
    }
  }

  // Grime streaks — breaks the perfect grid, reads as age
  ctx.globalAlpha = 0.08;
  for (let i = 0; i < 14; i++) {
    const x = rnd() * S;
    ctx.fillStyle = '#000';
    ctx.fillRect(x, rnd() * S * 0.3, 2 + rnd() * 6, S);
  }
  ctx.globalAlpha = 1;

  const t = toTexture(canvas);
  cache.set(key, t);
  return t;
}

// Lit-window grid for the skyline backdrop — used as an emissiveMap so the
// slabs read as a living city at dusk. Black = unlit; warm pixels = lit rooms.
export function litWindowsTexture(seed = 1, litRatio = 0.35) {
  const key = `windows:${seed}:${litRatio}`;
  if (cache.has(key)) return cache.get(key);

  const rnd = mulberry32(seed * 104729);
  const S = 128;
  const canvas = makeCanvas(S);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, S, S);

  const cols = 6, rows = 12;
  const cw = S / cols, rh = S / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (rnd() > litRatio) continue;
      const warm = rnd();
      ctx.fillStyle = warm > 0.7 ? '#ffd9a0' : warm > 0.3 ? '#ffb25e' : '#c8d8ff';
      ctx.globalAlpha = 0.5 + rnd() * 0.5;
      ctx.fillRect(c * cw + cw * 0.22, r * rh + rh * 0.25, cw * 0.56, rh * 0.5);
    }
  }
  ctx.globalAlpha = 1;

  const t = toTexture(canvas);
  cache.set(key, t);
  return t;
}

// Clone a cached texture with a repeat tuned to the mesh's real-world size so
// brick courses / window floors keep a constant physical scale per building.
export function withRepeat(texture, rx, ry) {
  const t = texture.clone();
  t.repeat.set(rx, ry);
  t.needsUpdate = true;
  return t;
}
